import { formatDateMedium, formatWeekdayShort } from '../../utils/timeUtils';
import { toDateKey } from './routyContext';

/**
 * Builds a deterministic "DATE FACTS" reference block for Routy's system prompt.
 *
 * Routy (the LLM) is known to get weekday↔date arithmetic wrong — it will call a
 * date the wrong weekday, or resolve "this Friday" to a Saturday. Rather than let
 * the model count days, we compute every relative-date phrase here from a single
 * tenant-shifted `now` and hand the model a verbatim table to read from. This
 * mirrors the existing "VERIFIED DATE FACTS" pattern in
 * `describeNonWorkingDayAlternatives`.
 *
 * Resolution conventions (all deterministic and documented so behavior is pinned):
 * - "this <weekday>"  = the next occurrence of that weekday on or after today
 *                       (within the next 7 days). When today IS that weekday,
 *                       "this <weekday>" = today.
 * - "next <weekday>"  = "this <weekday>" + 7 days.
 * - "this weekend"    = the upcoming Saturday + Sunday (each on or after today).
 * - "next week"       = Monday–Sunday of the following calendar week (week starts
 *                       Monday).
 * - "Upcoming 14 days" line removes weekday↔date arithmetic entirely for ANY
 *                       phrasing, even ones not enumerated above.
 *
 * `now` must already be tenant-shifted (its local getters hold the tenant's
 * wall-clock digits, per `tenantShiftedNow`) so every row is tenant-correct.
 */
export function buildRelativeDateReference(now: Date): string {
  // Long weekday names indexed by getDay() (0 = Sunday).
  const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // Shift by whole calendar days using local parts. JS Date normalizes month/year
  // rollover, so this is off-by-one-safe across month and year boundaries. Kept
  // local to this module so it works identically on web and mobile (mobile's
  // timeUtils has no Date-based addDays).
  const shiftDays = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

  const label = (date: Date): string => `${formatDateMedium(date)} (${toDateKey(date)})`;

  const dow = now.getDay();

  // Next occurrence of weekday `target` (0-6) on or after today (0-6 days out).
  const thisWeekday = (target: number): Date => shiftDays(now, (target - dow + 7) % 7);

  const lines: string[] = [];
  lines.push(
    'DATE FACTS (authoritative — resolve every date/weekday from these lines VERBATIM; ' +
      'NEVER count days or weekday names yourself):'
  );
  lines.push(`- today = ${label(now)}`);
  lines.push(`- tomorrow = ${label(shiftDays(now, 1))}`);

  for (let target = 0; target < 7; target++) {
    const d = thisWeekday(target);
    lines.push(`- this ${WEEKDAY_NAMES[target]} = ${label(d)}`);
  }
  for (let target = 0; target < 7; target++) {
    const d = shiftDays(thisWeekday(target), 7);
    lines.push(`- next ${WEEKDAY_NAMES[target]} = ${label(d)}`);
  }

  const saturday = thisWeekday(6);
  const sunday = thisWeekday(0);
  lines.push(`- this weekend = ${label(saturday)} and ${label(sunday)}`);

  // Monday of the following calendar week (never today, even if today is Monday).
  const mondayDelta = ((1 - dow + 7) % 7) || 7;
  const nextWeekMonday = shiftDays(now, mondayDelta);
  const nextWeekSunday = shiftDays(nextWeekMonday, 6);
  lines.push(`- next week = ${label(nextWeekMonday)} through ${label(nextWeekSunday)}`);

  const upcoming: string[] = [];
  for (let i = 0; i < 14; i++) {
    const d = shiftDays(now, i);
    upcoming.push(`${toDateKey(d)} ${formatWeekdayShort(d)}`);
  }
  lines.push(`Upcoming 14 days: ${upcoming.join(', ')}`);

  return lines.join('\n');
}
